import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../api/client';
import { BottomNav } from '../../components/app/DarkTheme';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Calendar, CreditCard, TrendingUp, Clock, AlertTriangle, ChevronLeft, ChevronRight, ChevronRight as ArrowRight, X } from 'lucide-react';
import { toast } from 'sonner';

const MemberHome = () => {
  const navigate = useNavigate();
  const { token, member, logout, showPinWarning, dismissPinWarning } = useAuth();
  const [homeData, setHomeData] = useState(null);
  const [nextClass, setNextClass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState('');
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [attendanceDates, setAttendanceDates] = useState([]);

  useEffect(() => {
    if (!token) {
      navigate('/app/login');
      return;
    }
    fetchHomeData();
    fetchNextClass();
  }, [token, navigate]);

  // Countdown timer
  useEffect(() => {
    if (!nextClass) return;
    
    const updateCountdown = () => {
      const now = new Date();
      const classTime = new Date(nextClass.scheduled_at);
      const diff = classTime - now;
      
      if (diff <= 0) {
        setCountdown('곧 시작');
        return;
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      if (days > 0) {
        setCountdown(`${days}일 ${hours}시간`);
      } else if (hours > 0) {
        setCountdown(`${hours}시간 ${minutes}분`);
      } else {
        setCountdown(`${minutes}분`);
      }
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);
    return () => clearInterval(interval);
  }, [nextClass]);

  const fetchHomeData = async () => {
    try {
      const response = await apiClient.get('/app/home', { params: { token } });
      setHomeData(response.data);
      // Initial month's attendance dates (server-provided)
      if (Array.isArray(response.data.attendance_dates)) {
        setAttendanceDates(response.data.attendance_dates);
      }
    } catch (error) {
      console.error('Failed to fetch home data:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/app/login');
      }
    } finally {
      setLoading(false);
    }
  };

  // Refetch attendance dates whenever the calendar month changes
  useEffect(() => {
    if (!token) return;
    const y = calendarMonth.getFullYear();
    const m = String(calendarMonth.getMonth() + 1).padStart(2, '0');
    apiClient
      .get('/app/attendance-calendar', { params: { token, month: `${y}-${m}` } })
      .then(res => setAttendanceDates(res.data?.dates || []))
      .catch(err => {
        console.error('Failed to fetch attendance calendar:', err);
        setAttendanceDates([]);
      });
  }, [calendarMonth, token]);

  const fetchNextClass = async () => {
    try {
      const response = await apiClient.get('/app/my-reservations', { 
        params: { token, status: 'upcoming' } 
      });
      if (response.data.reservations?.length > 0) {
        setNextClass(response.data.reservations[0]);
      }
    } catch (error) {
      console.error('Failed to fetch next class:', error);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const parts = new Intl.DateTimeFormat('ko-KR', {
      timeZone: 'Asia/Seoul',
      month: 'numeric', day: 'numeric', weekday: 'short',
    }).formatToParts(date);
    const get = (t) => parts.find(p => p.type === t)?.value || '';
    return `${get('month')}/${get('day')} (${get('weekday')})`;
  };

  const formatTime = (isoString) => {
    if (!isoString) return '--:--';
    const date = new Date(isoString);
    return date.toLocaleTimeString('ko-KR', { timeZone: 'Asia/Seoul', hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const formatTodayDate = () => {
    const today = new Date();
    const month = today.getMonth() + 1;
    const day = today.getDate();
    const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
    return `${month}월 ${day}일 ${dayNames[today.getDay()]}요일`;
  };

  const getPassDaysRemaining = (endDate) => {
    if (!endDate) return 0;
    const end = new Date(endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    const diff = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  // Calendar helpers
  const calendarData = useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    
    const weeks = [];
    let currentDay = 1 - startDayOfWeek;
    
    for (let w = 0; w < 6; w++) {
      const week = [];
      for (let d = 0; d < 7; d++) {
        if (currentDay > 0 && currentDay <= daysInMonth) {
          week.push(currentDay);
        } else {
          week.push(null);
        }
        currentDay++;
      }
      if (week.some(d => d !== null)) {
        weeks.push(week);
      }
    }
    
    return { year, month, weeks };
  }, [calendarMonth]);

  const isAttendedDate = (day) => {
    if (!day) return false;
    const dateStr = `${calendarData.year}-${String(calendarData.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return attendanceDates.includes(dateStr);
  };

  const isToday = (day) => {
    const today = new Date();
    return day === today.getDate() && 
           calendarData.month === today.getMonth() && 
           calendarData.year === today.getFullYear();
  };

  const monthNames = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];

  const getTotalScore = () => {
    if (!homeData?.fitness_snapshot) return 0;
    const fs = homeData.fitness_snapshot;
    const scores = [
      fs.strength_score || 0,
      fs.muscular_endurance_score || 0,
      fs.cardio_score || 0,
      fs.flexibility_score || 0,
      fs.body_composition_score || 0
    ];
    const validScores = scores.filter(s => s > 0);
    if (validScores.length === 0) return 0;
    return Math.round(validScores.reduce((a, b) => a + b, 0) / validScores.length);
  };

  const hasScoreData = () => {
    if (!homeData?.fitness_snapshot) return false;
    const fs = homeData.fitness_snapshot;
    return (fs.strength_score || 0) > 0 || 
           (fs.muscular_endurance_score || 0) > 0 || 
           (fs.cardio_score || 0) > 0 ||
           (fs.flexibility_score || 0) > 0 ||
           (fs.body_composition_score || 0) > 0;
  };

  // Pass expiry banner logic - MUST be before early returns to keep hook order stable
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const earlyActivePass = homeData?.passes?.find(p => p.status === 'active' || p.status === 'held');
  useEffect(() => {
    if (!earlyActivePass) return;
    const today = new Date().toISOString().slice(0, 10);
    const passKey = `pass-banner-${earlyActivePass.id || earlyActivePass._id || earlyActivePass.name}`;
    const dismissedAt = localStorage.getItem(passKey);
    setBannerDismissed(dismissedAt === today);
  }, [earlyActivePass]);

  const banner = useMemo(() => {
    if (!earlyActivePass || bannerDismissed) return null;
    if (earlyActivePass.status === 'held') return null;

    // Count-based: ≤3 remaining shows banner
    if (earlyActivePass.pass_type === 'count') {
      const rc = earlyActivePass.remaining_count ?? 0;
      if (rc <= 0 || rc > 3) return null;
      return {
        tone: rc === 1 ? 'urgent' : 'warn',
        icon: rc === 1 ? '🔔' : '⏰',
        title: `잔여 횟수 ${rc}회 남았습니다`,
        desc: '이용권이 곧 소진됩니다',
      };
    }

    // Period-based
    const days = getPassDaysRemaining(earlyActivePass.end_date);
    if (days < 0 || days > 7) return null;
    const endDate = new Date(earlyActivePass.end_date).toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' });
    if (days === 0) {
      return { tone: 'urgent', icon: '🚨', title: '이용권이 오늘 만료됩니다', desc: '새 이용권을 등록해주세요' };
    }
    if (days <= 3) {
      return { tone: 'urgent', icon: '🔔', title: `이용권이 ${days}일 남았습니다`, desc: `${endDate}에 종료됩니다` };
    }
    return { tone: 'warn', icon: '⏰', title: `이용권이 ${days}일 남았습니다`, desc: `${endDate}에 종료됩니다` };
  }, [earlyActivePass, bannerDismissed]);

  const dismissBanner = () => {
    if (!earlyActivePass) return;
    const today = new Date().toISOString().slice(0, 10);
    const passKey = `pass-banner-${earlyActivePass.id || earlyActivePass._id || earlyActivePass.name}`;
    localStorage.setItem(passKey, today);
    setBannerDismissed(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#0F1115]">
        <div className="text-[#52525B]">로딩 중...</div>
      </div>
    );
  }

  const { passes, fitness_snapshot } = homeData || {};
  const activePass = earlyActivePass;

  return (
    <div className="min-h-screen bg-[#0F1115] pb-24">
      {/* PIN Warning Dialog */}
      <Dialog open={showPinWarning} onOpenChange={dismissPinWarning}>
        <DialogContent className="max-w-sm bg-[#1A1D23] border-[#2A2E38] text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <AlertTriangle className="h-5 w-5 text-[#F59E0B]" />
              PIN 변경 안내
            </DialogTitle>
          </DialogHeader>
          <div className="py-3">
            <p className="text-[#A1A1AA] text-sm leading-relaxed">
              현재 PIN이 기본값으로 설정되어 있습니다.
              보안을 위해 <span className="text-white">설정</span>에서 PIN을 변경해주세요.
            </p>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={dismissPinWarning} className="flex-1 bg-transparent border-[#3A3F4A] text-[#A1A1AA] hover:bg-[#22252E] hover:text-white">
              나중에
            </Button>
            <Button onClick={() => { dismissPinWarning(); navigate('/app/settings'); }} className="flex-1 bg-[#FF7A00] hover:bg-[#FF8C42] border-0">
              변경하기
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="px-5 pt-12 pb-4">
        <p className="text-[#52525B] text-sm">{formatTodayDate()}</p>
        <h1 className="text-xl font-bold mt-1">
          <span className="text-[#FF7A00]">{member?.name}</span>
          <span className="text-white">님, 안녕하세요</span>
        </h1>
      </div>

      <div className="px-4 space-y-3">
        {/* Pass expiry banner */}
        {banner && (
          <div
            role="button"
            tabIndex={0}
            onClick={() => navigate('/app/passes')}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate('/app/passes'); }}
            data-testid="pass-expiry-banner"
            className={`rounded-2xl p-3.5 flex items-center gap-3 cursor-pointer active:scale-[0.99] transition-transform border ${
              banner.tone === 'urgent'
                ? 'bg-[#EF4444]/10 border-[#EF4444]/30'
                : 'bg-[#F59E0B]/10 border-[#F59E0B]/25'
            }`}
          >
            <span className="text-xl flex-shrink-0">{banner.icon}</span>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold ${banner.tone === 'urgent' ? 'text-[#FCA5A5]' : 'text-[#FBBF24]'}`}>
                {banner.title}
              </p>
              <p className="text-[11px] text-[#A1A1AA] mt-0.5">{banner.desc}</p>
            </div>
            <ArrowRight className="h-4 w-4 text-[#71717A] flex-shrink-0" />
            <button
              onClick={(e) => { e.stopPropagation(); dismissBanner(); }}
              className="p-1 -mr-1 text-[#71717A] hover:text-[#A1A1AA] active:scale-90 transition-transform"
              aria-label="오늘 하루 숨기기"
              data-testid="banner-dismiss-btn"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Hero Card: Next Class */}
        <div 
          data-testid="next-class-card" 
          className="bg-gradient-to-br from-[#1A1D23] to-[#22252E] rounded-2xl p-6 relative overflow-hidden"
        >
          {nextClass ? (
            <>
              {/* Subtle glow */}
              <div className="absolute -top-16 -right-16 w-32 h-32 bg-[#FF7A00]/8 rounded-full blur-3xl" />
              
              <div className="relative">
                <p className="text-[#FF7A00]/80 text-xs uppercase tracking-wider mb-3">다음 수업</p>
                
                <h3 className="text-xl font-bold text-white">{nextClass.class_name}</h3>
                
                <div className="flex items-center gap-3 mt-3">
                  <div className="flex items-center text-[#A1A1AA] text-sm">
                    <Calendar className="h-4 w-4 mr-1.5 text-[#52525B]" />
                    {formatDate(nextClass.scheduled_at)}
                  </div>
                  <div className="flex items-center text-[#A1A1AA] text-sm">
                    <Clock className="h-4 w-4 mr-1.5 text-[#52525B]" />
                    {formatTime(nextClass.scheduled_at)}
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-[#2A2E38]">
                  <div className="flex items-center justify-between">
                    <span className="text-[#52525B] text-sm">수업까지</span>
                    <span className="text-2xl font-bold text-[#FF7A00]">{countdown}</span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-6">
              <p className="text-[#52525B] mb-4">예정된 수업이 없습니다</p>
              <button 
                onClick={() => navigate('/app/book')} 
                data-testid="go-book-btn"
                className="bg-[#FF7A00] hover:bg-[#FF8C42] text-white font-medium px-6 py-2.5 rounded-xl transition-colors active:scale-[0.98]"
              >
                수업 예약하기
              </button>
            </div>
          )}
        </div>

        {/* Info Cards Row */}
        <div className="grid grid-cols-2 gap-3">
          {/* Pass Card */}
          <div
            data-testid="pass-card"
            className="bg-[#1A1D23] rounded-2xl p-4 cursor-pointer active:scale-[0.98] transition-transform"
            onClick={() => navigate('/app/passes')}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-[#52525B]" />
                <span className="text-[#71717A] text-xs">이용권</span>
              </div>
              <ArrowRight className="h-4 w-4 text-[#3A3F4A]" />
            </div>
            
            {activePass ? (
              <>
                {(() => {
                  const todayStr = new Date().toISOString().split('T')[0];
                  const holding = activePass.status === 'held' ||
                    (activePass.holding_start && activePass.holding_end &&
                     activePass.holding_start <= todayStr && todayStr <= activePass.holding_end);
                  const numColor = holding ? 'text-[#A78BFA]' : 'text-[#FF7A00]';
                  return (
                    <>
                      <p className="text-white text-sm font-medium truncate">{activePass.name}</p>
                      <p className="text-[#52525B] text-[10px] mt-0.5">
                        {(() => {
                          const fmt = (s) => {
                            const d = new Date(s);
                            return `${d.getFullYear()}.${String(d.getMonth()+1).padStart(2,'0')}.${String(d.getDate()).padStart(2,'0')}`;
                          };
                          return `${fmt(activePass.start_date)} ~ ${fmt(activePass.end_date)}`;
                        })()}
                      </p>
                      <div className="mt-2">
                        {activePass.pass_type === 'count' ? (
                          <div className="flex items-baseline gap-1">
                            <span className={`text-2xl font-bold ${numColor}`}>{activePass.remaining_count}</span>
                            <span className="text-[#52525B] text-sm">회</span>
                          </div>
                        ) : (
                          <div className="flex items-baseline gap-1">
                            <span className={`text-2xl font-bold ${numColor}`}>{getPassDaysRemaining(activePass.end_date)}</span>
                            <span className="text-[#52525B] text-sm">일</span>
                          </div>
                        )}
                        {holding && (
                          <p className="text-[#A78BFA] text-[11px] font-medium mt-1" data-testid="pass-card-holding-label">
                            홀딩 중
                            {activePass.holding_end && (() => {
                              const d = new Date(activePass.holding_end);
                              const label = `${d.getFullYear()}.${String(d.getMonth()+1).padStart(2,'0')}.${String(d.getDate()).padStart(2,'0')}`;
                              return (
                                <span className="text-[#A78BFA]/80 ml-1 font-normal">~{label}까지</span>
                              );
                            })()}
                          </p>
                        )}
                        {!holding && activePass.pass_type !== 'count' && getPassDaysRemaining(activePass.end_date) <= 7 && getPassDaysRemaining(activePass.end_date) > 0 && (
                          <p className="text-[#EF4444] text-xs mt-2">만료 임박</p>
                        )}
                      </div>
                    </>
                  );
                })()}
              </>
            ) : (
              <p className="text-[#52525B] text-sm">이용권 없음</p>
            )}
          </div>

          {/* Score Card */}
          <div 
            data-testid="score-card" 
            className={`bg-[#1A1D23] rounded-2xl p-4 relative overflow-hidden ${hasScoreData() ? 'cursor-pointer active:scale-[0.98] transition-transform' : ''}`}
            onClick={() => hasScoreData() && navigate('/app/score')}
          >
            {/* Subtle orange glow for score card */}
            {hasScoreData() && (
              <div className="absolute -bottom-8 -right-8 w-24 h-24 bg-[#FF7A00]/8 rounded-full blur-2xl" />
            )}
            
            <div className="relative">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-[#FF7A00]/60" />
                  <span className="text-[#71717A] text-xs">피트니스 점수</span>
                </div>
                {hasScoreData() && <ArrowRight className="h-4 w-4 text-[#3A3F4A]" />}
              </div>
              
              {hasScoreData() ? (
                <div className="flex items-center gap-3">
                  <MiniRadarChart data={fitness_snapshot} />
                  <div>
                    <div className="text-2xl font-bold text-[#FF7A00]">{getTotalScore()}</div>
                    <div className="text-[#52525B] text-xs">종합</div>
                  </div>
                </div>
              ) : (
                <div className="text-[#52525B] text-sm">측정 대기중</div>
              )}
            </div>
          </div>
        </div>

        {/* Attendance Calendar */}
        <div data-testid="attendance-calendar" className="bg-[#1A1D23] rounded-2xl p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-[#52525B]" />
              <span className="text-white text-sm font-medium">출석 기록</span>
            </div>
            <div className="flex items-center gap-1">
              <button 
                onClick={() => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))} 
                className="p-1 hover:bg-[#22252E] rounded-lg transition-colors"
              >
                <ChevronLeft className="h-4 w-4 text-[#52525B]" />
              </button>
              <span className="text-xs text-[#A1A1AA] min-w-[70px] text-center">
                {calendarData.year}.{String(calendarData.month + 1).padStart(2, '0')}
              </span>
              <button 
                onClick={() => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))} 
                className="p-1 hover:bg-[#22252E] rounded-lg transition-colors"
              >
                <ChevronRight className="h-4 w-4 text-[#52525B]" />
              </button>
            </div>
          </div>
          
          {/* Day headers */}
          <div className="grid grid-cols-7 gap-0.5 mb-1">
            {['일', '월', '화', '수', '목', '금', '토'].map((day, idx) => (
              <div key={day} className={`text-center text-[10px] py-1 ${
                idx === 0 ? 'text-[#EF4444]/60' : idx === 6 ? 'text-[#3B82F6]/60' : 'text-[#52525B]'
              }`}>
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar grid */}
          <div className="space-y-0.5">
            {calendarData.weeks.map((week, wIdx) => (
              <div key={wIdx} className="grid grid-cols-7 gap-0.5">
                {week.map((day, dIdx) => (
                  <div key={dIdx} className="aspect-square flex flex-col items-center justify-center">
                    {day && (
                      <>
                        <span className={`
                          w-6 h-6 flex items-center justify-center rounded-full text-xs
                          ${isToday(day) ? 'bg-white text-[#0F1115] font-bold' : ''}
                          ${!isToday(day) ? (
                            dIdx === 0 ? 'text-[#EF4444]/50' : dIdx === 6 ? 'text-[#3B82F6]/50' : 'text-[#71717A]'
                          ) : ''}
                        `}>
                          {day}
                        </span>
                        {/* Attendance dot */}
                        {isAttendedDate(day) && (
                          <span className="w-1 h-1 bg-[#FF7A00] rounded-full mt-0.5"></span>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
          
          {/* Legend */}
          <div className="flex items-center justify-end mt-3 gap-3 text-[10px]">
            <div className="flex items-center gap-1">
              <span className="w-2 h-2 bg-white rounded-full"></span>
              <span className="text-[#52525B]">오늘</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-[#FF7A00] rounded-full"></span>
              <span className="text-[#52525B]">출석</span>
            </div>
          </div>
        </div>
      </div>

      <BottomNav activeTab="home" />
    </div>
  );
};

// Mini Radar Chart (smaller, cleaner with subtle glow)
const MiniRadarChart = ({ data }) => {
  const size = 56;
  const center = size / 2;
  const radius = 22;
  
  const categories = [
    // Match MemberScore.js CAT_ORDER: 근력 → 심폐 → 체성분 → 체형 → 근지구력 (시계방향, 12시부터)
    { key: 'strength_score' },
    { key: 'cardio_score' },
    { key: 'body_composition_score' },
    { key: 'flexibility_score' },
    { key: 'muscular_endurance_score' }
  ];
  
  const getPoint = (index, value) => {
    const angle = (Math.PI * 2 * index) / categories.length - Math.PI / 2;
    const r = (value / 100) * radius;
    return { x: center + r * Math.cos(angle), y: center + r * Math.sin(angle) };
  };
  
  const points = categories.map((cat, i) => getPoint(i, data?.[cat.key] || 0));
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';
  
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <defs>
        <filter id="miniGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="1.5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      
      {/* Grid */}
      {[50, 100].map(level => {
        const gridPoints = categories.map((_, i) => getPoint(i, level));
        const gridPath = gridPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';
        return <path key={level} d={gridPath} fill="none" stroke="#2A2E38" strokeWidth="1" />;
      })}
      
      {/* Data with glow */}
      <path d={pathD} fill="rgba(255, 122, 0, 0.2)" stroke="#FF7A00" strokeWidth="1.5" filter="url(#miniGlow)" />
    </svg>
  );
};

export default MemberHome;
